import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scales, Envelope } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

export default function TermsPage() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: "By accessing or using NeoMaestro (the 'Platform'), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our Platform."
    },
    {
      title: "2. Platform Description",
      content: "NeoMaestro is a discovery and lead-generation marketplace that connects learners with expert-led masterclasses. We facilitate connections between learners and instructors but do not directly provide educational services."
    },
    {
      title: "3. User Accounts",
      subsections: [
        {
          subtitle: "Account Creation",
          content: "You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials."
        },
        {
          subtitle: "Account Types",
          items: [
            "Learners: Can browse and discover masterclasses",
            "Instructors: Can list and promote their masterclasses",
            "Admins: Platform administrators with full access"
          ]
        },
        {
          subtitle: "Account Security",
          content: "You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use."
        }
      ]
    },
    {
      title: "4. Instructor/Trainer Terms",
      items: [
        "Instructors are solely responsible for the content, quality, and delivery of their masterclasses",
        "Instructors must provide accurate information about their qualifications and masterclass details",
        "Instructors are responsible for managing their own bookings, payments, and refunds",
        "Instructors must comply with all applicable laws and regulations",
        "Masterclass listings are subject to admin approval before going live",
        "We reserve the right to remove any listing that violates our policies"
      ]
    },
    {
      title: "5. Learner Terms",
      items: [
        "Learners access masterclasses through external registration links provided by instructors",
        "The Platform is not responsible for the quality or delivery of masterclasses",
        "Payment terms and conditions are determined by individual instructors",
        "Learners agree to use the Platform in good faith and not engage in fraudulent activities"
      ]
    },
    {
      title: "6. Payment and Refunds",
      items: [
        "Payments are processed directly between learners and instructors through external links",
        "The Platform does not handle payment processing in the current phase",
        "Refund policies are determined by individual instructors",
        "We are not responsible for payment disputes between learners and instructors",
        "Future payment integration will be subject to updated terms"
      ]
    },
    {
      title: "7. Content and Intellectual Property",
      subsections: [
        {
          subtitle: "User Content",
          content: "Instructors retain ownership of their masterclass content. By listing on our Platform, instructors grant us a license to display and promote their content."
        },
        {
          subtitle: "Platform Content",
          content: "All Platform content, design, and functionality are owned by NeoMaestro and protected by intellectual property laws."
        },
        {
          subtitle: "Copyright Compliance",
          content: "Users must not upload or share content that infringes on others' intellectual property rights."
        }
      ]
    },
    {
      title: "8. Prohibited Activities",
      content: "Users are strictly prohibited from:",
      items: [
        "Posting false, misleading, or fraudulent information",
        "Engaging in harassment, abuse, or discriminatory behavior",
        "Attempting to circumvent the Platform to avoid fees (when applicable)",
        "Scraping or copying Platform content without permission",
        "Using the Platform for illegal activities",
        "Creating fake accounts or impersonating others",
        "Spamming or sending unsolicited communications"
      ]
    },
    {
      title: "9. Platform Fees",
      content: "Currently, NeoMaestro operates as a free discovery platform. Future monetization may include:",
      items: [
        "Featured listing fees for instructors",
        "Subscription plans for premium features",
        "Commission on bookings facilitated through the Platform"
      ],
      footer: "Users will be notified in advance of any fee structure implementation."
    },
    {
      title: "10. Disclaimers",
      items: [
        "The Platform is provided 'as is' without warranties of any kind",
        "We do not guarantee the accuracy, quality, or availability of masterclasses",
        "We are not responsible for interactions between learners and instructors",
        "We do not endorse or verify instructor qualifications",
        "Use of the Platform is at your own risk"
      ]
    },
    {
      title: "11. Limitation of Liability",
      content: "To the maximum extent permitted by law:",
      items: [
        "NeoMaestro shall not be liable for any indirect, incidental, or consequential damages",
        "Our total liability shall not exceed the amount paid by you (if any) for Platform services",
        "We are not responsible for disputes between instructors and learners",
        "We are not liable for losses arising from Platform downtime or technical issues"
      ]
    },
    {
      title: "12. Termination",
      items: [
        "We reserve the right to suspend or terminate accounts that violate these Terms",
        "Instructors may close their accounts at any time",
        "Termination does not affect existing bookings or obligations",
        "Users are responsible for backing up their data before account closure"
      ]
    },
    {
      title: "13. Changes to Terms",
      content: "We may update these Terms and Conditions from time to time. Continued use of the Platform after changes constitutes acceptance of the updated Terms. Material changes will be communicated via email or Platform notifications."
    },
    {
      title: "14. Governing Law and Dispute Resolution",
      items: [
        "These Terms are governed by the laws of India",
        "Disputes shall be resolved through negotiation and mediation first",
        "If unresolved, disputes shall be subject to the exclusive jurisdiction of courts in [Your City/State]"
      ]
    },
    {
      title: "15. Indemnification",
      content: "You agree to indemnify and hold harmless NeoMaestro, its affiliates, and employees from any claims, damages, or expenses arising from your use of the Platform or violation of these Terms."
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0"
            data-testid="logo"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <div className="flex gap-6 items-center">
            <button onClick={() => navigate('/marketplace')} className="text-white/90 hover:text-white transition-colors">
              Browse
            </button>
            <button onClick={() => navigate('/about')} className="text-white/90 hover:text-white transition-colors">
              About
            </button>
            <button onClick={() => navigate('/')} className="text-white/90 hover:text-white transition-colors">
              Home
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 gradient-header">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <Scales size={32} className="text-white" weight="duotone" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 text-white" data-testid="terms-title">
              Terms & Conditions
            </h1>
            <p className="text-xl text-white/90">
              Please read these terms carefully before using our Platform
            </p>
            <p className="text-sm text-white/80 mt-4">
              Last Updated: January 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 p-6 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <p className="text-gray-800 font-medium">
              ⚠️ Important: By using NeoMaestro, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </motion.div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="bg-gray-50 rounded-xl p-8 border border-gray-200"
              >
                <h2 className="text-2xl font-bold mb-4 text-gray-900">{section.title}</h2>
                
                {section.content && (
                  <p className="text-gray-700 mb-4">{section.content}</p>
                )}

                {section.subsections && (
                  <div className="space-y-4">
                    {section.subsections.map((sub, subIndex) => (
                      <div key={subIndex}>
                        <h3 className="font-semibold text-gray-900 mb-2">{sub.subtitle}</h3>
                        {sub.content && <p className="text-gray-700 mb-2">{sub.content}</p>}
                        {sub.items && (
                          <ul className="space-y-2 ml-4">
                            {sub.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="flex items-start text-gray-700">
                                <span className="text-[#00a3af] mr-2">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {section.items && !section.subsections && (
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start text-gray-700">
                        <span className="text-[#00a3af] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.footer && (
                  <p className="text-gray-700 mt-4 italic">{section.footer}</p>
                )}
              </motion.div>
            ))}

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#00a3af]/10 to-[#34c759]/10 rounded-xl p-8 border border-[#00a3af]/20"
            >
              <h2 className="text-2xl font-bold mb-4 text-gray-900">16. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms and Conditions, please contact us:
              </p>
              <a 
                href="mailto:contact@neomaestro.in"
                className="inline-flex items-center gap-2 text-[#00a3af] hover:text-[#004d7a] font-semibold transition-colors"
              >
                <Envelope size={24} weight="duotone" />
                contact@neomaestro.in
              </a>
            </motion.div>

            {/* Final Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#004d7a]/5 rounded-xl p-6 border border-[#004d7a]/20 text-center"
            >
              <p className="text-gray-800 font-medium mb-2">
                Thank you for using NeoMaestro
              </p>
              <p className="text-gray-600 text-sm">
                We're committed to providing a safe and valuable platform for learning and teaching.
              </p>
            </motion.div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/marketplace')}
              className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-12 py-6 text-lg font-bold"
            >
              Explore Masterclasses
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="gradient-footer text-white py-12 px-6 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold mb-4">Company</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors">Browse Courses</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Legal</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Get Started</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/auth?role=instructor')} className="hover:text-white transition-colors">Teach a Masterclass</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/70 text-sm">© 2026 NeoMaestro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
